import {
    nextTick, onBeforeUnmount, onMounted,
} from 'vue';
import {
    decodeStrict, resolveKeyPress, trimScan,
} from '@/utils/scannerKeyboardLayout';
import type { ScannerKeyPressType, ScannerScanType } from '@/utils/scannerKeyboardLayout';

export type { ScannerKeyPressType, ScannerScanType };

export type ScannerBurstType = ScannerScanType & {
    isLongEnough: boolean;
    isFast: boolean;
};

export type ScannerInputOptionsType = {

    /** Average gap between key presses that still counts as machine input. */
    maxAverageKeyIntervalMs?: number;

    /** Gap after which the buffer is treated as a new burst. */
    burstResetMs?: number;

    /** Shortest burst that may override the typed value. */
    minLength?: number;

    /** Silence after which a burst that never got a terminating key is closed. */
    idleMs?: number;

    /**
     * Called once the burst ends on its own. Many scanners send no Enter (or any other)
     * suffix at all, so waiting for one would drop the scan entirely.
     */
    onBurstEnd?: (burst: ScannerBurstType) => void;
};

/**
 * Averaged over a whole code this separates the two cleanly: a scanner sends a character
 * every five to fifteen milliseconds, while fifty is around 240 words per minute, well
 * past anyone typing a code by hand. Averaging also survives a single hiccup mid-burst,
 * which comparing the longest gap did not.
 */
const DEFAULT_MAX_AVERAGE_KEY_INTERVAL_MS = 50;
const DEFAULT_BURST_RESET_MS = 600;
const DEFAULT_MIN_LENGTH = 4;
const DEFAULT_IDLE_MS = 250;

/**
 * Keys that mean the text is being edited rather than entered, so the burst is abandoned.
 *
 * Tab is deliberately absent: plenty of scanners send it instead of Enter, and abandoning the
 * burst on it dropped every scan those made.
 */
const EDITING_KEYS = new Set([
    'Backspace',
    'Delete',
    'Escape',
    'ArrowLeft',
    'ArrowRight',
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End',
    'PageUp',
    'PageDown',
]);

/**
 * Makes an Enter-terminated scan field independent of the operating system keyboard
 * layout.
 *
 * Every key press is mirrored into a buffer built from the physical key rather than
 * the character the OS composed (see `resolveUsLayoutChar`). The buffer only replaces
 * the typed value when the whole burst arrived at machine speed, so manual typing and
 * pasting — which produces no key presses at all — keep working unchanged.
 */
export const useScannerInput = (options: ScannerInputOptionsType = {}) => {
    const maxAverageKeyIntervalMs = options.maxAverageKeyIntervalMs ?? DEFAULT_MAX_AVERAGE_KEY_INTERVAL_MS;
    const burstResetMs = options.burstResetMs ?? DEFAULT_BURST_RESET_MS;
    const minLength = options.minLength ?? DEFAULT_MIN_LENGTH;
    const idleMs = options.idleMs ?? DEFAULT_IDLE_MS;
    const { onBurstEnd } = options;

    let buffer: ScannerKeyPressType[] = [];
    let lastKeyAt = 0;
    let intervalSum = 0;
    let intervalCount = 0;
    let idleTimer: ReturnType<typeof setTimeout> | null = null;

    const cancelIdle = (): void => {
        if (idleTimer !== null) {
            clearTimeout(idleTimer);
            idleTimer = null;
        }
    };

    const reset = (): void => {
        cancelIdle();
        buffer = [];
        lastKeyAt = 0;
        intervalSum = 0;
        intervalCount = 0;
    };

    const handleKeydown = (event: KeyboardEvent): void => {
        // A scanner never sends modifier combinations; a shortcut or paste ends the burst.
        if (event.ctrlKey || event.altKey || event.metaKey) {
            reset();

            return;
        }

        const press = resolveKeyPress(event);

        if (press === null) {
            // Modifier, lock and function keys can turn up inside a burst without ending it, and
            // a terminating key closes it so it can be read. Only editing the text abandons it.
            if (EDITING_KEYS.has(event.key)) {
                reset();
            }

            return;
        }

        const interval = lastKeyAt === 0 ? 0 : event.timeStamp - lastKeyAt;

        if (interval > burstResetMs) {
            buffer = [];
            intervalSum = 0;
            intervalCount = 0;
        } else if (lastKeyAt !== 0) {
            intervalSum += interval;
            intervalCount += 1;
        }

        buffer.push(press);
        lastKeyAt = event.timeStamp;

        if (onBurstEnd !== undefined) {
            cancelIdle();
            idleTimer = setTimeout(() => {
                idleTimer = null;
                onBurstEnd(takeBurst());
            }, idleMs);
        }
    };

    /**
     * The buffered burst and whether it arrived at machine speed. Consumes the buffer.
     */
    function takeBurst(): ScannerBurstType {
        const keys = buffer;
        const value = decodeStrict(keys);
        const isLongEnough = value.length >= minLength;
        const isFast = intervalCount > 0 && intervalSum / intervalCount <= maxAverageKeyIntervalMs;

        reset();

        return { value, keys, isLongEnough, isFast };
    }

    /**
     * The scanned burst when it looks like machine input, otherwise the value the field holds
     * with no key presses behind it. Consumes the buffer either way.
     */
    const takeScan = (fallback: string): ScannerScanType => {
        const { value, keys, isLongEnough, isFast } = takeBurst();

        return isLongEnough && isFast ? { value, keys } : { value: fallback, keys: [] };
    };

    /**
     * Whether a burst is currently being collected.
     */
    const isCollecting = (): boolean => buffer.length > 0;

    onBeforeUnmount(cancelIdle);

    return { handleKeydown, takeScan, takeBurst, isCollecting };
};

/**
 * Suffixes a scanner appends to a code. Both close the burst so it can be read; which one a
 * scanner sends is a configuration setting nobody reliably knows, so both are accepted.
 */
const TERMINATING_KEYS = new Set(['Enter', 'Tab']);

/**
 * Attribute marking an input that rebuilds scanner bursts on its own, so the global
 * listener leaves it alone.
 */
export const SCANNER_INPUT_ATTRIBUTE = 'data-scanner-input';

export type GlobalScannerInputOptionsType = ScannerInputOptionsType & {

    /** Whether the page currently expects a scan. */
    isEnabled?: () => boolean;
};

type EditableSnapshotType = {
    element: HTMLInputElement | HTMLTextAreaElement;
    value: string;
};

const captureEditable = (target: EventTarget | null): EditableSnapshotType | null => {
    if (!(target instanceof HTMLInputElement) && !(target instanceof HTMLTextAreaElement)) {
        return null;
    }

    return { element: target, value: target.value };
};

const isOwnedByScanField = (target: EventTarget | null): boolean =>
    target instanceof Element && target.closest(`[${SCANNER_INPUT_ATTRIBUTE}]`) !== null;

/**
 * Listens for scanner bursts on the whole document, so a code can be scanned while the
 * operator has clicked away from the scan field.
 *
 * Nothing types into a page outside a text field, so a burst that lands there is taken as
 * a scan whatever its speed — scanners vary far too much for a timing limit to decide.
 * A burst that lands in another text field has to arrive at machine speed to be taken as a
 * scan rather than as typing, and that field is then restored to the value it held before
 * the burst, so scanning into the wrong input does not pollute it.
 */
export const useGlobalScannerInput = (
    onScan: (scan: ScannerScanType) => void,
    options: GlobalScannerInputOptionsType = {},
): void => {
    const { isEnabled, ...scannerOptions } = options;

    let pendingRestore: EditableSnapshotType | null = null;

    /**
     * Reports the burst unless it looks like someone typing, and puts back whatever text
     * field the characters landed in.
     */
    const complete = (burst: ScannerBurstType): boolean => {
        const restore = pendingRestore;

        pendingRestore = null;

        // Speed only tells a scan from typing, and only a text field can be typed into.
        if (!burst.isLongEnough || (restore !== null && !burst.isFast)) {
            return false;
        }

        const scanned = trimScan(burst);

        if (scanned.value === '') {
            return false;
        }

        if (restore !== null) {
            // After the flush: the burst already pushed its characters into the field's model,
            // and Vue would write them straight back over an immediate restore.
            void nextTick(() => {
                restore.element.value = restore.value;
                restore.element.dispatchEvent(new Event('input', { bubbles: true }));
            });
        }

        onScan(scanned);

        return true;
    };

    const { handleKeydown, takeBurst, isCollecting } = useScannerInput({
        ...scannerOptions,
        onBurstEnd: complete,
    });

    const listener = (event: KeyboardEvent): void => {
        if (isEnabled !== undefined && !isEnabled()) {
            return;
        }

        // The dedicated scan field rebuilds the burst itself.
        if (isOwnedByScanField(event.target)) {
            return;
        }

        const wasCollecting = isCollecting();

        handleKeydown(event);

        if (!wasCollecting && isCollecting()) {
            pendingRestore = captureEditable(event.target);
        }

        if (!TERMINATING_KEYS.has(event.key)) {
            return;
        }

        if (!complete(takeBurst())) {
            return;
        }

        // The terminating key belongs to the scan, not to whatever the page has focused — without
        // this an Enter would press the focused button or submit the surrounding form, and a Tab
        // would move the focus off the field the operator is scanning into.
        event.preventDefault();
        event.stopPropagation();
    };

    onMounted(() => {
        document.addEventListener('keydown', listener, true);
    });

    onBeforeUnmount(() => {
        document.removeEventListener('keydown', listener, true);
    });
};
